/* Sprint 5 client feedback 2026-06-20: Risk Assessment used to be a
 * fire-and-forget click — the analyst hit "Start Risk Assessment" on
 * the project workspace and nothing visible happened until the report
 * eventually appeared. DD and SPA both navigate to /processing/<jobId>
 * after upload; Risk had no equivalent.
 *
 * This page polls GET /projects/<auditId>/risk every 3 sec while the
 * run is in flight, mirrors the look of the DD/SPA Processing page
 * (banner + progress card + step list), and routes to the Risk Report
 * page once the run lands in a terminal state.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  getRiskAssessment,
  listRiskClaims,
  getExternalApiLog,
} from '../services/api';
import type { RiskAssessmentDetail } from '../types/api';

/* Phases the Stage 3 orchestrator walks through. We can't subscribe to
   real per-phase progress (the worker doesn't emit ticks), but we can
   infer how far along it is from the data that's accumulated so far. */
const PHASES = [
  { id: 'p1', label: 'Loading DD attributes' },
  { id: 'p2', label: 'Collecting verifiable claims' },
  { id: 'p3', label: 'External verification (Tavily + EDGAR)' },
  { id: 'p4', label: 'Intra-document contradictions' },
  { id: 'p5', label: 'Public-source enrichment' },
  { id: 'p6', label: 'Market probes' },
  { id: 'p7', label: 'Peer benchmark' },
  { id: 'p8', label: 'ISA opinion synthesis' },
  { id: 'p9', label: 'Cross-section narrative (Exec Summary / Findings / Strengths)' },
];

type PhaseStatus = 'done' | 'active' | 'pending';

function StatusBadge({ status }: { status: string }): JSX.Element {
  const variant =
    status === 'complete' ? 'badge-pass' :
    status === 'running' ? 'badge-info' :
    status === 'pending' ? 'badge-neutral' :
    status === 'failed' ? 'badge-fail' :
    status === 'degraded' ? 'badge-watch' :
    'badge-neutral';
  return (
    <span className={`badge ${variant}`}>
      ● {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

export function RiskProcessingPage(): JSX.Element {
  const { auditId } = useParams<{ auditId: string }>();
  const navigate = useNavigate();
  const [risk, setRisk] = useState<RiskAssessmentDetail | null>(null);
  const [claimsCount, setClaimsCount] = useState<{ total: number; confirmed: number; not_found: number }>({
    total: 0, confirmed: 0, not_found: 0,
  });
  const [apiCallCount, setApiCallCount] = useState<number>(0);
  const [error, setError] = useState<string | null>(null);
  const [elapsedSec, setElapsedSec] = useState<number>(0);

  /* Poll every 3 sec while running. Stops polling once we hit a
     terminal state (complete / failed / degraded). */
  useEffect(() => {
    if (!auditId) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    async function tick(): Promise<void> {
      try {
        const r = await getRiskAssessment(auditId!);
        if (cancelled) return;
        setRisk(r);
        // pick up live counts so the progress card shows actual numbers
        try {
          const cl = await listRiskClaims(auditId!);
          if (!cancelled) {
            setClaimsCount({
              total: cl.total,
              confirmed: cl.claims.filter((c) => c.status === 'confirmed' || c.status === 'consistent').length,
              not_found: cl.claims.filter((c) => c.status === 'not_found').length,
            });
          }
        } catch {
          /* claims endpoint may 404 early in the run; ignore */
        }
        try {
          const log = await getExternalApiLog(auditId!);
          if (!cancelled) setApiCallCount(log.calls.length);
        } catch {
          /* api log may be empty early */
        }
        if (['complete', 'failed', 'degraded'].includes(r.status)) {
          // terminal — stop polling
          return;
        }
      } catch (e) {
        if (!cancelled) {
          setError(e instanceof Error ? e.message : 'Failed to load Risk status');
        }
      }
      timer = setTimeout(tick, 3000);
    }

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [auditId]);

  /* Elapsed-time counter ticks every second so the analyst sees
     wall-clock progress even when the polling cycle hasn't fired. */
  useEffect(() => {
    if (!risk?.triggered_at) return;
    const start = new Date(risk.triggered_at).getTime();
    const end = risk.completed_at ? new Date(risk.completed_at).getTime() : null;
    if (end) {
      setElapsedSec(Math.round((end - start) / 1000));
      return;
    }
    const id = setInterval(() => {
      setElapsedSec(Math.round((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [risk?.triggered_at, risk?.completed_at]);

  if (error) {
    return (
      <div className="page-body">
        <div className="alert alert-fail">{error}</div>
        <button className="btn btn-secondary" style={{ marginTop: 'var(--s3)' }} onClick={() => navigate(`/projects/${auditId}`)}>
          ← Back to Project
        </button>
      </div>
    );
  }

  if (!risk) {
    return (
      <div className="page-body" style={{ textAlign: 'center', paddingTop: 80 }}>
        <span className="spinner spinner-lg" />
        <p className="t-muted t-sm" style={{ marginTop: 'var(--s3)' }}>
          Looking up Risk Assessment status…
        </p>
      </div>
    );
  }

  const isTerminal = ['complete', 'failed', 'degraded'].includes(risk.status);
  /* Phase inference — per-phase progress derived from the data each
     phase produces. Previously phases 4-7 all flipped to 'done' together
     when ai_opinion landed, so the percentage jumped 33% → 78% in one
     poll. Now each phase reads its own dedicated field on the Risk
     row (enrichment.rows, market_probes, peer_benchmark, etc.) so the
     bar ticks every 3-sec poll cycle as work accrues. */
  const enrichRows = (risk.enrichment as { rows?: unknown[] } | null)?.rows;
  const synthesisPresent = !!(risk.enrichment && (risk.enrichment as Record<string, unknown>).synthesis);
  const phaseStatuses: Record<string, PhaseStatus> = (() => {
    const m: Record<string, PhaseStatus> = {};
    if (risk.status === 'pending') {
      PHASES.forEach((p) => (m[p.id] = 'pending'));
      return m;
    }
    // Phase 1 — DD attributes load is done the moment the run started.
    m.p1 = 'done';
    // Phase 2 — claims collection.
    m.p2 = claimsCount.total > 0 ? 'done' : 'active';
    // Phase 3 — external verification (Tavily + EDGAR). Done when the
    // API call count matches at least the number of claims. The market
    // probes / enrichment also contribute API calls so this is a lower
    // bound but a usable signal.
    m.p3 = apiCallCount >= claimsCount.total && claimsCount.total > 0 ? 'done'
         : apiCallCount > 0 ? 'active' : (m.p2 === 'done' ? 'active' : 'pending');
    // Phase 4 — intra-document contradictions. No dedicated field;
    // assume done once enrichment OR market probes appear (both run
    // strictly after contradictions in the orchestrator).
    const afterContradictions =
      !!enrichRows || (Array.isArray(risk.market_probes) && risk.market_probes.length > 0);
    m.p4 = afterContradictions ? 'done' : (m.p3 === 'done' ? 'active' : 'pending');
    // Phase 5 — public-source enrichment. Done when enrichment.rows[] is populated.
    m.p5 = Array.isArray(enrichRows) && enrichRows.length > 0 ? 'done'
         : m.p4 === 'done' ? 'active' : 'pending';
    // Phase 6 — market probes. Done when market_probes[] is populated.
    m.p6 = Array.isArray(risk.market_probes) && risk.market_probes.length > 0 ? 'done'
         : m.p5 === 'done' ? 'active' : 'pending';
    // Phase 7 — peer benchmark. Done when peer_benchmark.peers[] is populated.
    const peers = (risk.peer_benchmark as { peers?: unknown[] } | null)?.peers;
    m.p7 = Array.isArray(peers) && peers.length > 0 ? 'done'
         : m.p6 === 'done' ? 'active' : 'pending';
    // Phase 8 — ISA opinion synthesis.
    m.p8 = risk.ai_opinion !== null ? 'done'
         : m.p7 === 'done' ? 'active' : 'pending';
    // Phase 9 — cross-section narrative synthesis.
    m.p9 = synthesisPresent ? 'done'
         : isTerminal ? 'done'
         : m.p8 === 'done' ? 'active' : 'pending';
    if (isTerminal) {
      // Force everything to 'done' on a terminal state so a degraded /
      // failed run also visually stops cleanly.
      PHASES.forEach((p) => { if (m[p.id] === 'active' || m[p.id] === 'pending') m[p.id] = 'done'; });
    }
    return m;
  })();

  /* Progress percentage — weight "done" phases as 1.0 and "active"
     phases as 0.4 so the bar ticks visibly within a phase too, not
     just at phase boundaries. Without partial weighting the bar can
     sit at the same percent for 30-60 seconds while a single phase
     (e.g. external verification) churns through dozens of Tavily
     calls. */
  const progressUnits = PHASES.reduce((acc, p) => {
    const st = phaseStatuses[p.id];
    if (st === 'done') return acc + 1;
    if (st === 'active') return acc + 0.4;
    return acc;
  }, 0);
  let progressPct = Math.min(100, Math.round((progressUnits / PHASES.length) * 100));
  /* Wall-clock floor — Stage 3 takes ~3-5 min end-to-end; if the bar
     sits at the same percentage for 30+ seconds because the polled
     phase-data hasn't advanced, nudge the displayed % upward based on
     elapsed time so the user sees motion. The data-derived bar takes
     over as soon as the next poll comes back with new evidence.
     Capped at 95% so we never claim "done" before the data confirms. */
  if (!isTerminal && risk.status !== 'pending') {
    const TYPICAL_RUN_S = 240; // 4 min
    const wallClockFloor = Math.min(95, Math.round((elapsedSec / TYPICAL_RUN_S) * 100));
    if (wallClockFloor > progressPct) {
      progressPct = wallClockFloor;
    }
  }

  return (
    <div className="module-risk">
      <div className="module-page-header">
        <div className="breadcrumb">
          <a href="/projects" onClick={(e) => { e.preventDefault(); navigate('/projects'); }}>Projects</a>
          <span className="bc-sep">/</span>
          <a href={`/projects/${auditId}`} onClick={(e) => { e.preventDefault(); navigate(`/projects/${auditId}`); }}>{auditId}</a>
          <span className="bc-sep">/</span>
          <span>Stage 3 (Risk) · Processing</span>
        </div>
        <div className="report-title-row">
          <div>
            <p className="module-eyebrow">RISK Module</p>
            <h1 className="t-h1">Risk Assessment</h1>
            <p className="t-xs t-muted" style={{ marginTop: 4 }}>
              {risk.risk_id} · triggered {new Date(risk.triggered_at).toLocaleString()}
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 'var(--s2)' }}>
            <StatusBadge status={risk.status} />
            <span className="t-xs t-muted">
              {elapsedSec >= 60
                ? `${Math.floor(elapsedSec / 60)} min ${elapsedSec % 60} sec`
                : `${elapsedSec} sec`} elapsed
            </span>
          </div>
        </div>
      </div>

      <div className="page-body">
        {/* Progress bar */}
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--s3)' }}>
            <span className="t-sm" style={{ fontWeight: 600 }}>{progressPct}% complete</span>
            <span className="t-xs t-muted">
              {claimsCount.total} claims · {apiCallCount} external API calls
              {risk.external_api_total_cost_usd > 0 && ` · $${risk.external_api_total_cost_usd.toFixed(4)}`}
            </span>
          </div>
          <div className="progress-bar">
            <div
              className={`progress-fill${risk.status === 'running' ? ' shimmer' : ''}`}
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>

        {/* Phase list */}
        <div className="card" style={{ marginTop: 'var(--s4)' }}>
          <h3 className="t-h3" style={{ marginBottom: 'var(--s3)' }}>Stage 3 phases</h3>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 8 }}>
            {PHASES.map((p) => {
              const st = phaseStatuses[p.id];
              return (
                <li
                  key={p.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    background:
                      st === 'done' ? 'color-mix(in srgb, var(--pass) 8%, transparent)'
                      : st === 'active' ? 'color-mix(in srgb, var(--info) 8%, transparent)'
                      : 'var(--surface-alt)',
                    borderRadius: 'var(--r-sm)',
                    fontSize: '0.85rem',
                  }}
                >
                  <span style={{ display: 'inline-flex', width: 18, justifyContent: 'center' }}>
                    {st === 'active' && <span className="spinner spinner-sm" />}
                    {st === 'done' && (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--pass)" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                    {st === 'pending' && (
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--lines)' }} />
                    )}
                  </span>
                  <span style={{ fontWeight: 500 }}>{p.label}</span>
                  <span
                    style={{
                      marginLeft: 'auto',
                      fontSize: '0.72rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      color:
                        st === 'done' ? 'var(--pass)'
                        : st === 'active' ? 'var(--info)'
                        : 'var(--muted)',
                    }}
                  >
                    {st === 'active' ? 'Running…' : st}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>

        {/* Terminal-state CTAs */}
        {isTerminal && (
          <div className="card" style={{ marginTop: 'var(--s4)' }}>
            {risk.status === 'complete' && (
              <>
                <h3 className="t-h3" style={{ marginBottom: 'var(--s2)', color: 'var(--pass)' }}>
                  ✓ Risk Assessment complete
                </h3>
                <p className="t-sm t-muted" style={{ marginBottom: 'var(--s3)' }}>
                  {claimsCount.total} claims verified · {apiCallCount} external API calls
                  {risk.external_api_total_cost_usd > 0 && ` · $${risk.external_api_total_cost_usd.toFixed(4)}`}
                </p>
                <div style={{ display: 'flex', gap: 'var(--s2)' }}>
                  <button className="btn btn-primary" onClick={() => navigate(`/projects/${auditId}/risk-report`)}>
                    View Draft Report →
                  </button>
                  <button className="btn btn-secondary" onClick={() => navigate(`/projects/${auditId}`)}>
                    ← Back to Project
                  </button>
                </div>
              </>
            )}
            {risk.status === 'degraded' && (
              <>
                <h3 className="t-h3" style={{ marginBottom: 'var(--s2)', color: 'var(--watch)' }}>
                  ⚠ Risk Assessment completed with degraded sections
                </h3>
                <p className="t-sm t-muted" style={{ marginBottom: 'var(--s3)' }}>
                  Some sections fell back to placeholders or external API budget was exhausted.
                  The report is still readable — check the Sources section for details.
                </p>
                {risk.degraded_sections && risk.degraded_sections.length > 0 && (
                  <ul style={{ marginBottom: 'var(--s3)', fontSize: '0.85rem', color: 'var(--text-sec)' }}>
                    {risk.degraded_sections.map((d, i) => (
                      <li key={i}>● {d.section}: {d.reason}</li>
                    ))}
                  </ul>
                )}
                <div style={{ display: 'flex', gap: 'var(--s2)' }}>
                  <button className="btn btn-primary" onClick={() => navigate(`/projects/${auditId}/risk-report`)}>
                    View Draft Report →
                  </button>
                  <button className="btn btn-secondary" onClick={() => navigate(`/projects/${auditId}`)}>
                    ← Back to Project
                  </button>
                </div>
              </>
            )}
            {risk.status === 'failed' && (
              <>
                <h3 className="t-h3" style={{ marginBottom: 'var(--s2)', color: 'var(--fail)' }}>
                  ✗ Risk Assessment failed
                </h3>
                <p className="t-sm t-muted" style={{ marginBottom: 'var(--s3)' }}>
                  The Stage 3 orchestrator did not complete. Common causes:
                  Ollama unreachable, Tavily key invalid, DD assessment row missing.
                  Check the celery-worker logs for the traceback.
                </p>
                <div style={{ display: 'flex', gap: 'var(--s2)' }}>
                  <button className="btn btn-secondary" onClick={() => navigate(`/projects/${auditId}`)}>
                    ← Back to Project
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
